import { useState, useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Card, CardContent, Label } from '@pantry/ui';
import { cn } from '@pantry/ui';
import { logger } from '../../lib/logger';
import {
  useUpdateItemInstance,
  WASTE_REASONS,
  WASTE_REASON_LABELS,
  getUnitCategory,
  convertQuantity,
  getQuickSelectOptions,
  getQuantityStepSize,
  formatQuantityDisplay,
  type WasteReason,
  type ItemUnit,
} from '@pantry/api-client';
import {
  XMarkIcon,
  MinusIcon,
  PlusIcon,
  TrashIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { UnitSelector } from './unit-selector';
import { UnitConversionTooltip } from './unit-conversion-tooltip';

interface WasteReasonDialogProps {
  householdId: string;
  itemId: string;
  instanceId: string;
  /** Total quantity in the instance. If > 1, shows quantity selector. */
  instanceQuantity?: number;
  /** Unit of the item (for display and unit switching). Defaults to 'item'. */
  unit?: ItemUnit;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Get icon for waste reason
 */
function getReasonIcon(reason: WasteReason) {
  switch (reason) {
    case 'expired':
      return ClockIcon;
    case 'spoiled':
      return ExclamationTriangleIcon;
    case 'damaged':
      return NoSymbolIcon;
    case 'other':
    default:
      return QuestionMarkCircleIcon;
  }
}

/**
 * Dialog for selecting a reason when marking an item as wasted.
 * Requires user to select a waste reason before submitting.
 */
export function WasteReasonDialog({
  householdId,
  itemId,
  instanceId,
  instanceQuantity = 1,
  unit = 'item',
  onClose,
  onSuccess,
}: WasteReasonDialogProps) {
  const { t } = useLingui();
  const [selectedReason, setSelectedReason] = useState<WasteReason | null>(null);
  const [statusNotes, setStatusNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [quantityToWaste, setQuantityToWaste] = useState(instanceQuantity);
  // Track the input unit (may differ from stored unit for unit switching)
  const [inputUnit, setInputUnit] = useState<ItemUnit>(unit);
  // Track max quantity in current display unit
  const [maxInDisplayUnit, setMaxInDisplayUnit] = useState(instanceQuantity);

  const updateInstance = useUpdateItemInstance();

  // Show quantity selector when instance has more than 1 item
  const showQuantitySelector = instanceQuantity > 1;

  // Check if unit switching is available (non-discrete units)
  const unitCategory = getUnitCategory(unit);
  const canSwitchUnits = unitCategory !== 'discrete';

  // Get step size based on unit type for +/- buttons
  const stepSize = getQuantityStepSize(inputUnit);

  const handleQuantityChange = (delta: number) => {
    setQuantityToWaste((prev) => {
      const newVal = prev + delta * stepSize;
      const rounded = Math.round(newVal * 1000) / 1000;
      return Math.max(stepSize, Math.min(maxInDisplayUnit, rounded));
    });
  };

  // Handle unit switching with quantity conversion
  const handleUnitChange = useCallback(
    (newUnit: ItemUnit) => {
      if (newUnit === inputUnit) return;

      // Convert the current quantity to the new unit
      const convertedQty = convertQuantity(quantityToWaste, inputUnit, newUnit);
      if (convertedQty !== null) {
        const rounded = Math.round(convertedQty * 1000) / 1000;
        setQuantityToWaste(rounded);
      }

      // Convert the max quantity to the new unit
      const convertedMax = convertQuantity(maxInDisplayUnit, inputUnit, newUnit);
      if (convertedMax !== null) {
        const roundedMax = Math.round(convertedMax * 1000) / 1000;
        setMaxInDisplayUnit(roundedMax);
      }

      setInputUnit(newUnit);
    },
    [inputUnit, quantityToWaste, maxInDisplayUnit]
  );

  // Get quick selection options based on unit type (common cooking amounts)
  const quickSelections = getQuickSelectOptions(inputUnit, maxInDisplayUnit);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedReason) {
      setError(t`Please select a reason for wasting this item`);
      return;
    }

    try {
      // Convert quantity back to stored unit if user switched units
      let finalQuantity = quantityToWaste;
      if (inputUnit !== unit && canSwitchUnits) {
        const converted = convertQuantity(quantityToWaste, inputUnit, unit);
        if (converted !== null) {
          finalQuantity = Math.round(converted * 1000) / 1000;
        }
      }

      // Check if wasting entire instance (compare in display unit with small tolerance for float precision)
      const isWastingAll = Math.abs(quantityToWaste - maxInDisplayUnit) < 0.001;

      await updateInstance.mutateAsync({
        householdId,
        itemId,
        instanceId,
        data: {
          status: 'wasted',
          wasteReason: selectedReason,
          statusNotes: statusNotes.trim() || undefined,
          // Only include quantityToConsume if not wasting entire instance
          ...(showQuantitySelector && !isWastingAll ? { quantityToConsume: finalQuantity } : {}),
        },
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      logger.error('Failed to mark as wasted', err, { householdId, itemId, instanceId });
      setError(err instanceof Error ? err.message : t`Failed to update item`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/20 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Card className="w-full max-w-md animate-in slide-in-from-bottom-4 duration-300 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-expired/10">
              <TrashIcon className="h-5 w-5 text-expired" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-lg text-foreground">
                <Trans>Mark as Wasted</Trans>
              </h2>
              <p className="text-sm text-muted-foreground">
                <Trans>Why are you discarding this?</Trans>
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-full hover:bg-muted"
            onClick={onClose}
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>

        <CardContent className="pt-0">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in slide-in-from-top-2">
                {error}
              </div>
            )}

            {/* Quantity selector (only shown when instance has multiple items) */}
            {showQuantitySelector && (
              <div className="space-y-4 p-4 rounded-xl bg-muted/30 border border-border/50">
                <Label className="text-center block text-sm font-medium">
                  <Trans>How much are you wasting?</Trans>
                </Label>
                <div className="flex items-center justify-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantityToWaste <= stepSize || updateInstance.isPending}
                    className="h-11 w-11 rounded-xl"
                  >
                    <MinusIcon className="h-5 w-5" />
                  </Button>
                  <div className="flex flex-col items-center min-w-[100px]">
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-bold text-foreground">
                        {formatQuantityDisplay(quantityToWaste, inputUnit)}
                      </span>
                      {/* Unit selector */}
                      {canSwitchUnits ? (
                        <UnitSelector
                          value={inputUnit}
                          onChange={handleUnitChange}
                          baseUnit={unit}
                        />
                      ) : null}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      <Trans>of {formatQuantityDisplay(maxInDisplayUnit, inputUnit)}</Trans>
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantityToWaste >= maxInDisplayUnit || updateInstance.isPending}
                    className="h-11 w-11 rounded-xl"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </Button>
                  {/* Conversion info button */}
                  {canSwitchUnits && <UnitConversionTooltip unit={inputUnit} />}
                </div>
                {/* Quick selection buttons - common cooking amounts */}
                <div className="flex justify-center gap-2 flex-wrap">
                  {quickSelections.map((option) => (
                    <Button
                      key={option.label}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantityToWaste(option.value)}
                      disabled={updateInstance.isPending}
                      className={cn(
                        'rounded-lg min-w-[48px]',
                        Math.abs(quantityToWaste - option.value) < 0.001 &&
                          'border-expired bg-expired/10 text-expired'
                      )}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Waste reason selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                <Trans>What happened?</Trans>
              </Label>
              <div className="grid gap-2">
                {WASTE_REASONS.map((reason, index) => {
                  const Icon = getReasonIcon(reason);
                  const isSelected = selectedReason === reason;
                  return (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => setSelectedReason(reason)}
                      className={cn(
                        'flex items-center w-full px-4 py-3 rounded-xl border text-left transition-all duration-200 animate-in fade-in',
                        isSelected
                          ? 'border-expired bg-expired/5 shadow-sm'
                          : 'border-border/50 bg-card hover:bg-muted/50 hover:border-border'
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-lg mr-3 transition-colors',
                          isSelected ? 'bg-expired/20' : 'bg-muted'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-5 w-5 transition-colors',
                            isSelected ? 'text-expired' : 'text-muted-foreground'
                          )}
                        />
                      </div>
                      <span
                        className={cn(
                          'flex-1 font-medium transition-colors',
                          isSelected ? 'text-foreground' : 'text-muted-foreground'
                        )}
                      >
                        {WASTE_REASON_LABELS[reason]}
                      </span>
                      {isSelected && (
                        <div className="h-6 w-6 rounded-full bg-expired flex items-center justify-center animate-in zoom-in duration-200">
                          <svg
                            className="h-4 w-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional notes */}
            <div className="space-y-2">
              <Label htmlFor="statusNotes" className="text-sm font-medium">
                <Trans>Notes</Trans>{' '}
                <span className="text-muted-foreground font-normal">
                  <Trans>(optional)</Trans>
                </span>
              </Label>
              <textarea
                id="statusNotes"
                className="flex min-h-[80px] w-full rounded-xl border border-border/50 bg-muted/30 px-4 py-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-expired/50 focus-visible:border-expired/50 transition-all resize-none"
                placeholder={t`Any additional details...`}
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                maxLength={500}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={onClose} className="rounded-xl">
                <Trans>Cancel</Trans>
              </Button>
              <Button
                type="submit"
                disabled={!selectedReason || updateInstance.isPending}
                className="rounded-xl bg-expired hover:bg-expired/90 text-white min-w-[140px]"
              >
                {updateInstance.isPending ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    <Trans>Saving...</Trans>
                  </div>
                ) : showQuantitySelector ? (
                  t`Waste ${formatQuantityDisplay(quantityToWaste, inputUnit)}`
                ) : (
                  <Trans>Mark as Wasted</Trans>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
